import { codeKind, formatCode, normalizeCode, reasonMessage, testCodeKind } from '@pl/domain';
import type { ReasonCode, ScanRequest, ScanResponse, SkuDto } from '@pl/domain';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMe, useSkus } from '../../lib/queries';
import { skuMatches } from '../../lib/skuSearch';
import { uuid } from '../../lib/uuid';
import { ErrorNote, RefetchNote, SkuTag, Spinner } from '../../ui/bits';
import { isLight } from '../../ui/color';
import { Icon } from '../../ui/Icon';
import { useQueue, useQueueState } from '../queue/QueueProvider';
import { RedScreen } from '../RedScreen';
import { announceRefusal } from '../refusalGuard';
import { type LocalResult, ResultPanel } from '../ResultPanel';
import { feedback } from '../scanner/feedback';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import s from './Receive.module.css';

type Step = 'pick' | 'scan' | 'summary';
type Source = 'truck' | 'stocktake' | 'other';

const SOURCES: { value: Source; label: string }[] = [
  { value: 'truck', label: 'Truck / container' },
  { value: 'stocktake', label: 'Stocktake' },
  { value: 'other', label: 'Other' },
];

/** What a refusal is called on the phone. The server's own message carries the detail under it. */
const RED_TITLE: Partial<Record<ReasonCode, string>> = {
  STICKER_SKU_MISMATCH: 'Wrong sticker',
  WRONG_SKU: 'Wrong color',
  ALREADY_SHIPPED: 'Already shipped',
  OTHER_SITE: 'Wrong site',
  UNKNOWN_CODE: 'Unknown sticker',
  INVALID_CODE: 'Unknown sticker',
  SKU_REQUIRED: 'Choose the SKU first',
  TEST_STICKER: 'Test sticker',
};
const redTitle = (reason: ReasonCode | null) => (reason && RED_TITLE[reason]) || "Can't use this box";

type Tone = 'ok' | 'warn' | 'bad';

/** A scan this screen sent. Its standing is read back off the queue, never cached here. */
interface SentScan {
  clientScanId: string;
  code: string;
  at: number;
}

const DOT: Record<Tone, string> = { ok: s.dotOk!, warn: s.dotWarn!, bad: s.dotBad! };

const recentKey = (userId: string) => `pl.recentSkus.${userId}`;

function readRecent(userId: string | undefined): string[] {
  if (!userId) return [];
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(recentKey(userId)) ?? '[]');
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string').slice(0, 5) : [];
  } catch {
    return []; // private mode, or a value someone else wrote
  }
}

function writeRecent(userId: string | undefined, skuId: string): void {
  if (!userId) return;
  try {
    localStorage.setItem(recentKey(userId), JSON.stringify([skuId, ...readRecent(userId).filter((id) => id !== skuId)].slice(0, 5)));
  } catch {
    /* the list just does not survive a reload */
  }
}

function ago(from: number, now: number): string {
  const secs = Math.max(0, Math.round((now - from) / 1000));
  if (secs < 5) return 'now';
  if (secs < 60) return `${secs} s ago`;
  const mins = Math.round(secs / 60);
  return mins < 60 ? `${mins} min ago` : `${Math.round(mins / 60)} h ago`;
}

/**
 * Putting a delivery into stock: choose the SKU, then scan every box sticker.
 *
 * Nothing here says how many boxes the delivery is supposed to hold — a count is only worth
 * having when the person doing it cannot read the answer off their own phone. What it does show,
 * always, is where each scan stands: in, waiting, or refused. A queue a worker cannot see is a
 * queue they cannot trust, and a worker who cannot trust it will keep their own paper list.
 */
export function Receive() {
  const me = useMe();
  const { site } = useScanSite();
  const skusQuery = useSkus();
  const queue = useQueue();
  const { pending, sending, version } = useQueueState('receive');

  const [step, setStep] = useState<Step>('pick');
  const [skuId, setSkuId] = useState<string | null>(null);
  const [pallet, setPallet] = useState<{ id: string; code: string } | null>(null);
  const [palletFirst, setPalletFirst] = useState(false);
  const [source, setSource] = useState<Source>('truck');
  const [reference, setReference] = useState('');
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<LocalResult | null>(null);
  /** Which scan the panel above is describing, so it can follow that scan when it settles. */
  const [resultFor, setResultFor] = useState<string | null>(null);
  const [red, setRed] = useState<{ title: string; message: string; code?: string } | null>(null);
  const [sent, setSent] = useState<SentScan[]>([]);
  /** Codes that never reached the queue because they are not our stickers. */
  const [unreadable, setUnreadable] = useState<{ code: string; at: number }[]>([]);
  const [now, setNow] = useState(() => Date.now());

  const searchId = useId();
  const sourceId = useId();
  const referenceId = useId();

  // Never call `setRed` directly — go through here. `RedScreen` takes over the screen, closing
  // any sheet elsewhere in the app shell (the queue's "Needs attention" sheet in ScanLayout) that
  // could be open behind it; that close and `RedScreen` mounting must land in the same render, or
  // the sheet's own focus trap — cleared only once the close actually commits — wins a beat it
  // has no business winning and pulls focus straight back into a sheet about to disappear.
  const showRed = useCallback((next: { title: string; message: string; code?: string }) => {
    announceRefusal();
    setRed(next);
  }, []);

  const userId = me.data?.user.id;
  const skus = useMemo(() => skusQuery.data ?? [], [skusQuery.data]);
  const skuById = useMemo(() => new Map(skus.map((item) => [item.id, item])), [skus]);
  const sku = skuId ? (skuById.get(skuId) ?? null) : null;

  const activity = sent.length + unreadable.length;
  // "6 s ago" that never moves is a small lie; this keeps the list honest without a busy timer.
  useEffect(() => {
    if (activity === 0) return;
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, [activity]);

  /**
   * The counter, the dots and the summary are all read back off the queue rather than remembered
   * at the moment of the scan. A box that was queued and accepted a minute later is a box that is
   * in; one that was queued and then refused is not. Caching the moment would show neither.
   */
  const boxes = useMemo(
    () =>
      sent.flatMap(({ clientScanId }) => {
        const item = queue.get(clientScanId);
        const response = item?.status === 'done' ? item.response : null;
        return response?.outcome === 'accepted' && response.box ? [{ skuId: response.box.skuId ?? item!.request.skuId ?? null }] : [];
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sent, queue, version],
  );

  const lastScans = useMemo(() => {
    const rows = [
      ...sent.map(({ clientScanId, code, at }) => {
        const item = queue.get(clientScanId);
        const tone: Tone =
          !item || item.status === 'pending'
            ? 'warn'
            : item.status === 'failed'
              ? 'bad'
              : item.response?.outcome === 'accepted'
                ? 'ok'
                : item.response?.outcome === 'rejected'
                  ? 'bad'
                  : 'warn';
        return { key: clientScanId, code: formatCode(code), at, tone };
      }),
      ...unreadable.map((row) => ({ key: `x${row.at}${row.code}`, code: row.code, at: row.at, tone: 'bad' as Tone })),
    ];
    return rows.sort((a, b) => b.at - a.at).slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent, unreadable, queue, version]);

  /**
   * The panel is the last scan's standing, not a souvenir of the moment it was taken. An amber
   * "saved on this phone" that stays amber after the scan has landed is the screen telling the
   * worker to worry about a box that is already in.
   */
  const shownResult = useMemo<LocalResult | null>(() => {
    if (!result || !resultFor || !site) return result;
    const item = queue.get(resultFor);
    if (!item || item.status === 'pending') return result;
    if (item.status === 'failed') return { tone: 'bad', title: 'Scan refused', message: item.error ?? 'The server would not take this scan.', code: result.code };
    const response = item.response;
    if (!response) return result;
    if (response.outcome === 'rejected') return { tone: 'bad', title: redTitle(response.reasonCode), message: response.message, code: result.code };
    if (response.outcome !== 'accepted') return { tone: 'warn', title: response.message, message: 'Nothing was added for this sticker.', code: result.code };
    return { tone: 'ok', title: response.message, message: response.pallet && !response.box ? 'Pallet ready.' : `Into stock at ${site.name}.`, code: result.code };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, resultFor, queue, site, version]);

  const handleCode = useCallback(
    async (raw: string) => {
      const at = Date.now();
      setNow(at);

      const code = normalizeCode(raw);
      if (!code) {
        // Nothing is sent: the server cannot make sense of this, and a round trip would only
        // delay telling the worker that what they scanned is not one of our stickers.
        feedback('bad');
        setUnreadable((prev) => [{ code: raw.slice(0, 20), at }, ...prev].slice(0, 5));
        setResult(null);
        setResultFor(null);
        // A test label is refused here and now, like any code that is not a box's: nothing to send.
        if (testCodeKind(raw)) showRed({ title: 'Test sticker', message: reasonMessage('TEST_STICKER'), code: raw.slice(0, 24) });
        else showRed({ title: 'Unknown sticker', message: "This doesn't look like one of our stickers.", code: raw.slice(0, 24) });
        return;
      }
      if (!site) return;

      const request: ScanRequest = {
        // Made here, at the moment of capture, and reused unchanged on every retry for the life
        // of this scan. That is what stops a resend from receiving the box twice.
        clientScanId: uuid(),
        siteId: site.id,
        mode: 'receive',
        code,
        skuId,
        palletId: pallet?.id ?? null,
        source,
        reference: reference.trim() || null,
        scannedAt: new Date().toISOString(),
      };
      setSent((prev) => [...prev, { clientScanId: request.clientScanId, code, at }]);
      setResultFor(request.clientScanId);
      const outcome = await queue.submit(request, 'receive');
      const pretty = formatCode(code);

      if (outcome.state === 'queued') {
        feedback('warn');
        // Storage is asked of the queue, not read off the render: the write that decides it
        // happens inside the submit above, so the value this screen rendered with is a scan old.
        if (codeKind(code) === 'P') {
          // The server can only attach boxes to a pallet it has activated. Holding a pallet id it
          // has never seen would put every box after this one onto a pallet that does not exist.
          setResult({ tone: 'warn', title: 'Pallet not confirmed yet', message: 'Boxes scanned now are received without a pallet.', code: pretty });
        } else if (queue.storageOk()) {
          setResult({ tone: 'warn', title: 'Saved on this phone', message: 'Will send when back online.', code: pretty });
        } else {
          setResult({ tone: 'warn', title: 'Waiting to send', message: 'This phone could not save it — keep this screen open until it goes.', code: pretty });
        }
        return;
      }

      // A red outcome sets the panel as well as the full-screen flash, and both carry the code of
      // the scan that was actually refused. The panel is what is left once the flash is dismissed:
      // if it still named the scan before this one, the worker would set aside a box that is fine
      // and let the refused one through — the opposite of what the screen is for.
      if (outcome.state === 'failed') {
        feedback('bad');
        setResult({ tone: 'bad', title: 'Scan refused', message: outcome.error.message, code: pretty });
        showRed({ title: 'Scan refused', message: outcome.error.message, code: pretty });
        return;
      }

      const response: ScanResponse = outcome.response;
      if (response.outcome === 'rejected') {
        feedback('bad');
        setResult({ tone: 'bad', title: redTitle(response.reasonCode), message: response.message, code: pretty });
        showRed({ title: redTitle(response.reasonCode), message: response.message, code: pretty });
        return;
      }
      if (response.outcome === 'duplicate' || response.outcome === 'ignored') {
        feedback('warn');
        setResult({ tone: 'warn', title: response.message, message: 'Nothing was added for this sticker.', code: pretty });
        return;
      }

      feedback('ok');
      if (response.pallet && !response.box) {
        setPallet({ id: response.pallet.id, code: response.pallet.code });
        setResult({ tone: 'ok', title: 'Pallet ready', message: response.message, code: formatCode(response.pallet.code) });
        return;
      }
      if (response.box) {
        const boxSkuId = response.box.skuId;
        // A printed sticker already knows its SKU, so scanning one is a way of choosing it.
        if (!skuId && boxSkuId) {
          setSkuId(boxSkuId);
          writeRecent(userId, boxSkuId);
        }
        const where = pallet ? `Into stock at ${site.name}, on pallet ${formatCode(pallet.code)}.` : `Into stock at ${site.name}.`;
        setResult({ tone: 'ok', title: response.message, message: where, code: pretty });
        return;
      }
      setResult({ tone: 'ok', title: response.message, message: `Recorded at ${site.name}.`, code: pretty });
    },
    [pallet, queue, reference, showRed, site, skuId, source, userId],
  );

  const shown = useMemo(() => {
    // The Office picker's rule, so "cat6 blue" finds CAT6 CMR 1000ft Blue here too.
    if (search.trim()) return skus.filter((item) => skuMatches(item, search));
    const recent = readRecent(userId).map((id) => skuById.get(id)).filter((item): item is SkuDto => Boolean(item));
    return recent.length > 0 ? recent : skus;
  }, [search, skuById, skus, userId]);

  if (!site) return null;

  if (step === 'summary') {
    return <Summary boxes={boxes} skuById={skuById} waiting={pending} onResume={() => setStep('scan')} />;
  }

  if (step === 'pick') {
    return (
      <>
        <div className={`scan-body ${s.pickBody}`}>
          <label className={s.srOnly} htmlFor={searchId}>
            Search SKU or color
          </label>
          <div className={`inp lg ${s.searchField}`}>
            <Icon name="search" />
            <input id={searchId} className={s.searchInput} type="search" placeholder="Search SKU or color" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
          </div>

          <div className="sect">{search.trim() ? 'Matches' : 'Recent'}</div>
          <RefetchNote queries={[skusQuery]} />
          {skusQuery.isPending ? (
            <div className={s.loading}>
              <Spinner label="Loading SKUs" />
            </div>
          ) : skusQuery.isLoadingError ? (
            <div className={s.errorPanel}>
              <ErrorNote error={skusQuery.error} />
              <button type="button" className={`bigbtn ghost ${s.retryBtn}`} onClick={() => void skusQuery.refetch()}>
                Try again
              </button>
            </div>
          ) : shown.length === 0 ? (
            <p className={s.noMatch}>No SKU matches that.</p>
          ) : (
            <div className="rows">
              {shown.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`row ${s.skuRow}`}
                  aria-pressed={item.id === skuId}
                  onClick={() => {
                    setSkuId(item.id);
                    writeRecent(userId, item.id);
                    setStep('scan');
                  }}
                >
                  <span className={`skux ${s.skuCell}`}>
                    <SkuTag sku={item} withCode />
                  </span>
                  <Icon name="chev-right" size={20} />
                </button>
              ))}
            </div>
          )}

          <button type="button" className={s.palletRow} aria-pressed={palletFirst} onClick={() => setPalletFirst((v) => !v)}>
            <span className={s.palletIcon}>
              <Icon name="pallet-load" size={22} />
            </span>
            <span className={s.palletText}>
              <b>Scan pallet sticker first</b>
              <small>{palletFirst ? 'Armed · scan the pallet sticker next' : 'Optional · groups the boxes on it'}</small>
            </span>
            <Icon name={palletFirst ? 'check' : 'chev-right'} size={20} />
          </button>

          <div className="sect">Where it came from</div>
          <div className={s.sourceGrid}>
            <label className={s.fieldLabel} htmlFor={sourceId}>
              Source
            </label>
            <select id={sourceId} className={`inp lg ${s.control}`} value={source} onChange={(e) => setSource(e.target.value as Source)}>
              {SOURCES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label className={s.fieldLabel} htmlFor={referenceId}>
              Reference
            </label>
            <input id={referenceId} className={`inp lg ${s.control}`} type="text" placeholder="Container or PO number" value={reference} onChange={(e) => setReference(e.target.value)} autoComplete="off" />
          </div>
        </div>

        <div className="scan-foot">
          <p className={`note ${s.scanTip}`}>
            <Icon name="scan" size={18} />
            Or scan any SKU sticker to pick its SKU
          </p>
          <button type="button" className="bigbtn ghost" onClick={() => setStep('scan')}>
            <Icon name="scan" />
            Scan a SKU sticker
          </button>
        </div>
      </>
    );
  }

  const light = isLight(sku?.colourHex);

  return (
    <>
      <div className={s.bandWrap}>
        <div className={`band ${s.colourBand}`} style={{ background: sku?.colourHex ?? 'var(--paper-2)', color: light ? 'var(--ink)' : '#fff' }}>
          <div className={s.bandSku}>
            <div className="clr-xl">{sku ? (sku.colourName ?? 'NO COLOR').toUpperCase() : 'ANY STICKER'}</div>
            <div className={`skul ${s.bandName}`}>
              {sku ? sku.name.toUpperCase() : 'A printed sticker picks its own SKU'}
              {sku && <span className="mono">{sku.code}</span>}
            </div>
          </div>
          <div className="n">
            <b data-testid="received-count">{boxes.length}</b>
            <span>received</span>
          </div>
        </div>
        <div className={s.bandMeta}>
          {pallet ? (
            <span className={s.palletChip}>
              <Icon name="pallet-load" size={17} />
              <span className="mono">{formatCode(pallet.code)}</span>
            </span>
          ) : palletFirst ? (
            <span className={s.palletChip}>
              <Icon name="pallet-load" size={17} />
              Scan the pallet sticker
            </span>
          ) : (
            <button type="button" className={s.changeSku} onClick={() => setStep('pick')}>
              Change SKU
            </button>
          )}
          {sending > 0 && <span className={s.sendingChip}>Sending…</span>}
          {pending > 0 && (
            <span className={s.waiting}>
              <Icon name="wifi-off" size={16} />
              {pending} waiting
            </span>
          )}
        </div>
      </div>

      <div className={`scan-body pt ${s.scanBody}`}>
        {/* The scanner stops while a red outcome holds the screen, so the next sticker cannot
            slip past unread and the Enter a wedge scanner sends cannot double as a dismissal. */}
        <ScannerView onCode={(raw) => void handleCode(raw)} enabled={red === null} />
        <ResultPanel result={shownResult} />

        {lastScans.length > 0 && (
          <>
            <div className="sect">
              Last scans<span className="r">Newest first</span>
            </div>
            <ul className={s.lastList}>
              {lastScans.map((scan) => (
                <li key={scan.key} className={s.lastRow}>
                  <span className={`${s.lastDot} ${DOT[scan.tone]}`} aria-hidden="true" />
                  <span className="mono">{scan.code}</span>
                  <span className={s.lastWhen}>{ago(scan.at, now)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="scan-foot">
        <button type="button" className="bigbtn" onClick={() => setStep('summary')}>
          Finish receiving
        </button>
      </div>

      {red && <RedScreen title={red.title} message={red.message} code={red.code} onContinue={() => setRed(null)} />}
    </>
  );
}

function Summary({
  boxes,
  skuById,
  waiting,
  onResume,
}: {
  boxes: { skuId: string | null }[];
  skuById: Map<string, SkuDto>;
  waiting: number;
  onResume: () => void;
}) {
  const perSku = useMemo(() => {
    const counts = new Map<string, number>();
    for (const box of boxes) counts.set(box.skuId ?? '', (counts.get(box.skuId ?? '') ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [boxes]);

  return (
    <>
      <div className={`scan-body ${s.summaryBody}`}>
        <h3 className={s.summaryTitle}>Received this session</h3>
        {perSku.length === 0 ? (
          <p className={s.noMatch}>No boxes were received.</p>
        ) : (
          <div className="rows">
            {perSku.map(([id, count]) => (
              <div key={id} className={`row ${s.summaryRow}`}>
                <span className={`skux ${s.skuCell}`}>
                  <SkuTag sku={skuById.get(id) ?? null} />
                </span>
                <b className={s.summaryCount}>{count}</b>
              </div>
            ))}
          </div>
        )}
        {waiting > 0 && (
          <p className={s.summaryWaiting}>
            <Icon name="wifi-off" size={20} />
            {waiting} still waiting to send
          </p>
        )}
        <p className={s.summaryNote}>Scans keep sending on their own, even after this screen is closed.</p>
      </div>
      <div className="scan-foot">
        <button type="button" className="bigbtn ghost" onClick={onResume}>
          Keep scanning
        </button>
        <Link to="/scan" className={`bigbtn ${s.doneLink}`}>
          Done
        </Link>
      </div>
    </>
  );
}
